import { getCurrentTenant, getSessionToken, useSession, useUser } from '@descope/react-sdk'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef } from 'react'

import { AppRoute } from '@/lib/url/urls'

const ORG_POLL_INTERVAL_MS = 60_000

// Reads the *current* active tenant from the current session token — NOT the in-memory useSession()
// copy. The request interceptor sends getSessionToken() fresh on every request, so this is the org
// the backend will actually act on. Returns '' when there's no readable token (e.g. mid-refresh).
const readCurrentTenant = (): string => {
  const token = getSessionToken()
  if (!token) return ''
  try {
    return getCurrentTenant(token) || ''
  } catch {
    return ''
  }
}

// Redirects a passive tab to the overview page when the active org (Descope `dct`) has been
// switched elsewhere, so its UI and its requests stay on the same org instead of drifting apart.
export const useActiveOrgGuard = () => {
  const { isAuthenticated, isSessionLoading } = useSession()
  const { user, isUserLoading } = useUser()
  const queryClient = useQueryClient()

  const bootOrgRef = useRef<string | undefined>(undefined)

  // Pin the org this tab started on, once auth has resolved. Never pin '' — protected-route's
  // auto-selectTenant may not have resolved a tenant yet.
  useEffect(() => {
    if (bootOrgRef.current) return
    if (isSessionLoading || isUserLoading || !isAuthenticated || !user?.userTenants?.length) return
    const current = readCurrentTenant()
    if (current) bootOrgRef.current = current
  }, [isAuthenticated, isSessionLoading, isUserLoading, user])

  const checkCurrentTenant = useCallback(() => {
    const boot = bootOrgRef.current
    if (!boot) return
    const current = readCurrentTenant()
    if (!current) return // ignore transient empty token during refresh
    if (current !== boot) {
      // Drop cached (old-org) data, then reload onto the new org's overview page — staying on the
      // current URL would keep old-org IDs the new org can't resolve.
      queryClient.clear()
      window.location.assign(AppRoute.OVERVIEW)
    }
  }, [queryClient])

  // Poll for a changed org on focus / visibility / interval — fires exactly when the user returns
  // to a stale tab and is about to act, and works across every browser.
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') checkCurrentTenant()
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('focus', checkCurrentTenant)
    const intervalId = window.setInterval(checkCurrentTenant, ORG_POLL_INTERVAL_MS)
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('focus', checkCurrentTenant)
      window.clearInterval(intervalId)
    }
  }, [checkCurrentTenant])
}
